/* eslint-disable indent */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  LinearProgress,
  ListItem,
  ListItemIcon,
  Divider,
  ListItemText,
  DialogActions,
  Tooltip,
  IconButton,
  TextField,
} from '@mui/material';
import Task from '@mui/icons-material/Task';
import Today from '@mui/icons-material/Today';

// components
import {
  AccessTime,
  AttachFile,
  Close,
  LocationOn,
} from '@mui/icons-material';

import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { editEventStatus } from '@pingum/app-state-management/eventStatus/actions';
import {
  changeStatus,
  patchEvent,
} from '@pingum/app-state-management/event/actions';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  DateTimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import AttachFileModal from '../../AttachFileModal';
import Property from '../../Property';
import EventUpload from '../../EventUpload';

// utils
import { formatDateString } from '../../../utils/dateHelper';

// local event page helper components
import EventActionsMenu from '../EventActionsMenu';

import { getContrast } from '../../../utils/scheduleHelper';
import { primary } from '../../../utils/brandColors';
import { TASK_MODAL_TYPE } from '../../../redux/app/types';
import EditTaskFields from '../EditTaskFields'
import HeaderSection from '../../Schedule/AddEditScheduleModal/components/HeaderSection';
import { getBarnHorse } from '../../Schedule/ScheduleTaskItem';
import { getLocationForTags } from '../../../utils/tagsHelper';
import TaskHighlightComponent from '../TaskHighlight';
import HorseIcon from '../../CustomIcons/Horse';
import BarnHorsesAutocomplete from '../../Autocompletes/BarnHorses';
import LocationAutocomplete from '../../Autocompletes/Locations';

const startingValidation = {
  name: true,
  completeDate: true,
  hasChanged: false,
};

const EventDataCardComponent = ({
  selectedEvent,
  schedule,
  isCalendarEvent,
  classes,
  isRestricted,
  canAttachFiles,
  showLoadingIndicator,
  isUpdatingEvent,
  selectedBarn,
  getEvent,
  handleClose,
}) => {
  const dispatch = useDispatch();

  // React state
  const [editMode, setEditMode] = React.useState(false)
  const [name, setName] = React.useState('');
  const [selectedEventBarnHorse, setSelectedEventBarnHorse] = React.useState(null);
  const [selectedEventLocation, setSelectedEventLocation] = React.useState(null);
  const [startDate, setStartDate] = React.useState(new Date());
  const [description, setDescription] = React.useState('')
  const [eventData, setEventData] = React.useState({})
  const [completeDate, setCompleteDate] = React.useState(null);
  const [currentStatusAssignment, setCurrentStatusAssignment] = React.useState(null);
  const [dueDate, setDueDate] = React.useState(null);
  const [tags, setTags] = React.useState([]);
  const [scheduleAssignment, setScheduleAssignment] = React.useState(null);
  const [validation, setValidation] = React.useState(startingValidation);

  const [openAttachFileModal, setOpenAttachFileModal] = React.useState(false);

  const openModalType = useSelector((state) => state.app.openModalType);
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const event = useSelector((state) => state.schedule.event);
  const eventTasks = useSelector((state) => state.schedule.eventTasks)
  const locationsMap = useSelector((state) => state.location.locationsMap)

  const resetData = () => {
    setEditMode(false);
    setName(selectedEvent.name || '')
    setDescription(selectedEvent.description || '')
    setCompleteDate(selectedEvent.completeDate || null)
    setDueDate(selectedEvent.dueDate || null)
    setTags(selectedEvent.tags || [])
    setEventData(selectedEvent.eventData || '')
    setScheduleAssignment(selectedEvent.scheduleAssignment || '');
    setCurrentStatusAssignment((selectedEvent && selectedEvent.currentEventStatus && selectedEvent.currentEventStatus.assignment) || null)
    setValidation(startingValidation);

    setSelectedEventBarnHorse(selectedEvent && selectedEvent.id
      && getBarnHorse(selectedEvent, null, barnHorsesMap, selectedBarn.barnMetadata.horseTagTypeId))
    setSelectedEventLocation((selectedEvent && selectedEvent.id
      && !selectedEventBarnHorse && getLocationForTags(
        selectedEvent.tags,
        selectedBarn.barnMetadata.locationTagTypeId,
        locationsMap,
      )) || null);
    setStartDate((selectedEvent && selectedEvent.startDate) || new Date())
  }

  React.useEffect(() => {
    resetData();
  }, [selectedEvent]);

  React.useEffect(() => {
    // Capture when assignment changes so we can show save button
    if (currentStatusAssignment) {
      setValidation({
        ...validation,
        hasChanged: true,
      })
    }
  }, [currentStatusAssignment]);

  const handleSaveEdit = () => {
    const request = {
      name,
      description,
      completeDate,
      tags: tags.map((t) => t.id),
      eventData,
      dueDate,
      scheduleAssignment,
      startDate,
    };

    dispatch(patchEvent(selectedEvent.id, request)).then(() => {
      if (currentStatusAssignment) {
        dispatch(editEventStatus(selectedEvent.currentEventStatus.id, { assignment: currentStatusAssignment })).then(() => {
          dispatch(getEvent(selectedEvent.id)); // reload so current event status is correct
        })
      }
      handleClose()
    });
  }

  const handleEventDataChange = (updatedEventData) => {
    setEventData(updatedEventData);
    setValidation({
      ...validation,
      hasChanged: true,
    })
  }

  const handleAttachFileClick = () => {
    setOpenAttachFileModal(true);
  }

  const handleCloseAttachFileModal = () => {
    setOpenAttachFileModal(false);
  }

  const handleNameUpdate = (updatedName) => {
    setName(updatedName);
    setValidation({
      ...validation,
      name: updatedName && updatedName.length >= 2 && updatedName.length < 100,
      hasChanged: true,
    })
  }

  const handleScheduleAssignmentChange = (updatedScheduleAssignment) => {
    setScheduleAssignment(updatedScheduleAssignment);
    setValidation({
      ...validation,
      hasChanged: true,
    })
  }

  const handleCompleteDateChange = (updatedCompleteDate) => {
    setCompleteDate(updatedCompleteDate);
    setValidation({
      ...validation,
      completeDate: updatedCompleteDate && updatedCompleteDate.getTime() > new Date(selectedEvent.startDate).getTime(),
      hasChanged: true,
    })
  }

  const handleEventStatus = (request, replacementTask, keepOpen = false) => {
    const task = replacementTask || selectedEvent;
    dispatch(patchEvent(task.id, request)).then(() => {
      const statusId = request.completeEvent
        ? selectedBarn.barnMetadata.statusEndId
        : selectedBarn.barnMetadata.statusPendingId;
      if (!keepOpen) {
        handleClose();
      }
      dispatch(changeStatus(task.id, {
        statusId: parseInt(statusId, 10),
        statusComment: null,
        completeEvent: request.completeEvent,
        assignment: (task.currentEventStatus && task.currentEventStatus.assignment) || '',
      }))
    });
  }

  const headerBackgroundColor = (schedule && schedule.metadata && schedule.metadata.calendarDisplayColor)
    || (schedule && schedule.eventTemplate && schedule.eventTemplate.eventDataDefaultValues
      && schedule.eventTemplate.eventDataDefaultValues.calendarDisplayColor)
    || (selectedEvent && selectedEvent.eventData && selectedEvent.eventData.Color && selectedEvent.eventData.Color.value)
    || primary.main;
  const headerTextColor = getContrast(headerBackgroundColor);
  const associatedTasks = (eventTasks) || []

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: headerBackgroundColor,
          color: `${headerTextColor} !important`,
          m: 1,
          borderRadius: 4,
        }}
      >
        <Grid container justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'nowrap' }}>

          <Grid item>

            <div
              className={classes.subheader}
              style={{
                color: headerTextColor,
                textWrap: 'auto',
                flex: 1,
              }}
            >
              {
                ((schedule && schedule.id) || (selectedEvent && selectedEvent.id))
                && (
                  <ListItem
                    dense
                    sx={{ p: 0 }}
                  >
                    <ListItemIcon sx={{ minWidth: 52 }}>
                      {
                        isCalendarEvent
                          ? (
                            <Today
                              sx={{
                                width: 40,
                                height: 40,
                                color: headerTextColor,
                              }}
                            />
                          )
                          : (
                            <Task
                              sx={{
                                width: 40,
                                height: 40,
                                color: headerTextColor,
                              }}
                            />
                          )
                      }
                    </ListItemIcon>
                    <Box>
                      <Typography
                        component="h3"
                        sx={{
                          margin: 0,
                          fontSize: '1.75rem',
                          fontWeight: 600,
                          lineHeight: '2rem',
                        }}
                      >
                        {selectedEvent.name}
                      </Typography>
                      <Typography
                        component="h5"
                        sx={{
                          margin: 0,
                          fontSize: '16px',
                          fontWeight: 400,
                          lineHeight: '1rem',
                        }}
                      >{formatDateString(selectedEvent.startDate)} - #{selectedEvent.id}
                      </Typography>
                    </Box>
                  </ListItem>
                )
              }
            </div>
          </Grid>
          {
            !isRestricted && (
              <Grid item>
                <Box sx={{ margin: 'auto' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                    <EventActionsMenu
                      attachFileClick={handleAttachFileClick}
                      onEdit={() => setEditMode(true)}
                      handleClose={handleClose}
                      editMode={editMode}
                      sx={{ color: headerTextColor }}
                    />
                    {
                      handleClose && (
                        <Tooltip title="Close Task">
                          <IconButton onClick={handleClose} role="button">
                            <Close sx={{ color: headerTextColor }} />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  </Box>
                </Box>
              </Grid>
            )
          }
        </Grid>
      </DialogTitle>
      <DialogContent
        sx={{
          pb: 0,
          px: 1,
          flex: 1,
        }}
      >
        {
          showLoadingIndicator && (
            <Box
              sx={{
                width: '100%',
                mb: 2,
              }}
            >
              <LinearProgress />
            </Box>
          )
        }

        {
          editMode
            ? (
              <Box>
                <ListItem dense>
                  <ListItemIcon>
                    {selectedEventBarnHorse
                      ? <HorseIcon />
                      : <LocationOn />}
                  </ListItemIcon>
                  <ListItemText>
                    {selectedEventBarnHorse && (
                      <BarnHorsesAutocomplete
                        multiple={false}
                        label="Horse"
                        value={selectedEventBarnHorse}
                        blurOnSelect
                        onSelect={(e, newHorse) => {
                          if (newHorse) {
                            setTags([...tags.filter((t) => t.id !== parseInt(selectedEventBarnHorse.pingumTagId, 10)),
                            {
                              id: parseInt(newHorse.pingumTagId, 10),
                              name: newHorse.horse.name,
                              tagType: { id: selectedBarn.barnMetadata.horseTagTypeId },
                            }])
                            setSelectedEventBarnHorse(newHorse);
                            setValidation({
                              ...validation,
                              hasChanged: true,
                            });
                          }
                        }}
                        limitTags={1}
                      />
                    )}
                    {selectedEventLocation && (
                      <LocationAutocomplete
                        multiple={false}
                        label="Location"
                        value={selectedEventLocation}
                        onSelect={(e, newLocations) => {
                          if (newLocations) {
                            setTags([...tags.filter((t) => t.id !== parseInt(selectedEventLocation.pingumTagId, 10)),
                            {
                              id: parseInt(newLocations.pingumTagId, 10),
                              name: newLocations.name,
                              tagType: { id: selectedBarn.barnMetadata.locationTagTypeId },
                            }])
                            setSelectedEventLocation(newLocations)
                            setValidation({
                              ...validation,
                              hasChanged: true,
                            });
                          }
                        }}
                        size="small"
                        limitTags={1}
                      />
                    )}
                  </ListItemText>

                </ListItem>
                <ListItem dense>
                  <ListItemIcon><AccessTime /></ListItemIcon>
                  <ListItemText>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DateTimePicker
                        renderInput={(props) => <TextField {...props} label="Task Start" size="small" />}
                        value={startDate}
                        onChange={(e) => {
                          const updatedDate = (e && e.toDate)
                            ? e.toDate()
                            : null;
                          if (updatedDate) {
                            setStartDate(updatedDate)
                            setValidation({
                              ...validation,
                              hasChanged: true,
                            });
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </ListItemText>

                </ListItem>
                <HeaderSection
                  validation={validation}
                  schedule={schedule}
                  event={event}
                  name={name}
                  setName={handleNameUpdate}
                  description={description}
                  setDescription={(newDescription) => {
                    setValidation({
                      ...validation,
                      hasChanged: true,
                    });
                    setDescription(newDescription);
                  }}
                  duration={(event && event.resource && event.resource.duration) || 0}
                  workflows={(event && event.resource && event.resource.workflows) || []}
                  additionalTags={tags}
                  setAdditionalTags={(newTags) => {
                    setValidation({
                      ...validation,
                      hasChanged: true,
                    });
                    setTags(newTags);
                  }}
                  detailedLocation={(event && event.resource && event.resource.location) || ''}
                  assignment={(event && event.resource && event.resource.assignment) || 'Default'}
                  type={(event && event.resource && event.resource.type) || ''}
                  disabledForBeingInPast
                />
                <EditTaskFields
                  selectedEvent={selectedEvent}
                  selectedBarn={selectedBarn}
                  barnHorsesMap={barnHorsesMap}
                  getEvent={getEvent}
                  dueDate={dueDate}
                  setDueDate={setDueDate}
                  validation={validation}
                  setValidation={setValidation}
                  completeDate={completeDate}
                  handleCompleteDateChange={handleCompleteDateChange}
                  handleEventDataChange={handleEventDataChange}
                  currentStatusAssignment={currentStatusAssignment}
                  setCurrentStatusAssignment={setCurrentStatusAssignment}
                  scheduleAssignment={scheduleAssignment}
                  handleScheduleAssignmentChange={handleScheduleAssignmentChange}
                />

                {
                  canAttachFiles
                  && selectedEvent.eventUploads
                  && selectedEvent.eventUploads.length > 0
                  && (
                    <ListItem dense>
                      <ListItemIcon><AttachFile /></ListItemIcon>
                      <ListItemText>
                        <Property disableGutters label="Task Attachments">
                          {
                            selectedEvent.eventUploads.map((upload) => (
                              <div
                                className={classes.eventUploadContainer}
                                key={upload.id}
                              >
                                <EventUpload eventUpload={upload} editable />
                              </div>
                            ))
                          }
                        </Property>
                      </ListItemText>
                    </ListItem>
                  )
                }

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                  }}
                >
                  <Button size="small" color="secondary" onClick={() => setEditMode(false)}>Cancel Edit Task</Button>
                </Box>

              </Box>
            )
            : (
              <TaskHighlightComponent
                task={selectedEvent}
                currentStatusAssignment={currentStatusAssignment}
                detailedLocation={(event && event.resource && event.resource.location) || ''}
                scheduleAssignment={scheduleAssignment}
                canAttachFiles={canAttachFiles}
                handleClose={handleClose}
                handleEventStatus={handleEventStatus}
                isUpdatingEvent={isUpdatingEvent}
                selectedEventBarnHorse={selectedEventBarnHorse}
                selectedEventLocation={selectedEventLocation}
                onEdit={() => setEditMode(true)}
                associatedTasks={associatedTasks}
              />
            )
        }

      </DialogContent>
      {
        editMode
        && (
          <DialogActions
            sx={{
              px: 1,
              flexDirection: 'column',
              gap: 1,
              width: '100%',
            }}
          >
            <Divider sx={{ width: '100%' }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              {
                openModalType === TASK_MODAL_TYPE && (
                  <Button color="secondary" sx={{ mr: 1 }} onClick={handleClose}>Cancel</Button>
                )
              }
              {
                openModalType !== TASK_MODAL_TYPE && validation.hasChanged && (
                  <Button
                    color="secondary"
                    sx={{ mr: 1 }}
                    onClick={resetData}
                    disabled={isUpdatingEvent || !validation.name || !validation.completeDate || !validation.hasChanged}
                  >
                    Cancel
                  </Button>
                )
              }
              <Button
                onClick={handleSaveEdit}
                disabled={isUpdatingEvent || !validation.name || !validation.completeDate || !validation.hasChanged}
                color="primary"
                variant="contained"
              >
                Save
              </Button>
            </Box>
          </DialogActions>
        )
      }
      <AttachFileModal
        isOpen={openAttachFileModal}
        handleCloseModal={handleCloseAttachFileModal}
        eventId={selectedEvent.id}
      />
    </Box>
  );
}

EventDataCardComponent.propTypes = {
  selectedEvent: PropTypes.object,
  schedule: PropTypes.object,
  isCalendarEvent: PropTypes.bool,
  classes: PropTypes.object,
  tags: PropTypes.array,
  eventData: PropTypes.array,
  isRestricted: PropTypes.bool,
  canAttachFiles: PropTypes.bool,
  showLoadingIndicator: PropTypes.bool,
  getEvent: PropTypes.func,
  handleClose: PropTypes.any,
  isUpdatingEvent: PropTypes.bool,
  selectedBarn: PropTypes.object,
};

EventDataCardComponent.defaultProps = {
  selectedEvent: {},
  isCalendarEvent: false,
  schedule: null,
  classes: {},
  tags: [],
  eventData: [],
  selectedBarn: {},
  isRestricted: false,
  canAttachFiles: false,
  showLoadingIndicator: false,
  getEvent: () => { },
  handleClose: null,
  isUpdatingEvent: false,
};

export default EventDataCardComponent;
